import { useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import type { MeterSearch, MeterSearchRow } from '@metron/shared';
import {
  METER_SEARCH_DEFAULT_PAGE_SIZE,
  parseMeterSearchParams,
  toMeterSearchParams,
} from '@metron/shared';
import { DataTable } from '../components/data-table/data-table.tsx';
import { DataTableColumnHeader } from '../components/data-table/data-table-column-header.tsx';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card.tsx';
import { MeterSearchFilters } from '../components/meter-search/MeterSearchFilters.tsx';
import { useTableUrlState } from '../hooks/useTableUrlState.ts';
import { useMeterFilter } from '../hooks/useMeterFilter.ts';
import { useCustomers } from '../hooks/useCustomers.ts';

const na = <span className='text-muted-foreground'>N/A</span>;

function rateCell(value: number | null) {
  return value == null ? na : <div className='text-right'>{value.toFixed(2)}</div>;
}

// Column ids match the spec's sortBy values, so useTableUrlState's `sortBy`
// param is directly the spec's. Customer isn't a server sort key.
const columns: ColumnDef<MeterSearchRow>[] = [
  {
    accessorKey: 'deviceId',
    header: ({ column }) => <DataTableColumnHeader column={column} title='Device ID' />,
    cell: ({ row }) => <div className='font-medium'>{row.original.deviceId}</div>,
  },
  {
    accessorKey: 'customerName',
    enableSorting: false,
    header: ({ column }) => <DataTableColumnHeader column={column} title='Customer' />,
    cell: ({ row }) => row.original.customerName ?? na,
  },
  {
    accessorKey: 'address',
    header: ({ column }) => <DataTableColumnHeader column={column} title='Address' />,
    cell: ({ row }) => row.original.address || na,
  },
  {
    accessorKey: 'consumerName',
    header: ({ column }) => <DataTableColumnHeader column={column} title='Consumer' />,
    cell: ({ row }) => row.original.consumerName || na,
  },
  {
    accessorKey: 'rate',
    header: ({ column }) => <DataTableColumnHeader column={column} title='Rate (GPM)' />,
    cell: ({ row }) => rateCell(row.original.rate),
  },
  {
    accessorKey: 'leakRate',
    header: ({ column }) => <DataTableColumnHeader column={column} title='Leak (GPM)' />,
    cell: ({ row }) => rateCell(row.original.leakRate),
  },
  {
    accessorKey: 'backflowRate',
    header: ({ column }) => <DataTableColumnHeader column={column} title='Backflow (GPM)' />,
    cell: ({ row }) => rateCell(row.original.backflowRate),
  },
  {
    // What the contact filters use (MET-3555): last device communication.
    accessorKey: 'lastContact',
    header: ({ column }) => <DataTableColumnHeader column={column} title='Last Contact' />,
    cell: ({ row }) => {
      const value = row.original.lastContact;
      return value
        ? new Date(value).toLocaleString()
        : <span className='text-muted-foreground'>Never</span>;
    },
  },
  {
    accessorKey: 'lastReadDate',
    header: ({ column }) => <DataTableColumnHeader column={column} title='Last Read' />,
    cell: ({ row }) => {
      const value = row.original.lastReadDate;
      return value
        ? new Date(value).toLocaleString()
        : <span className='text-muted-foreground'>Never</span>;
    },
  },
];

/**
 * Meter Search (Smart Search results, MET-3507)
 *
 * The whole search lives in the URL (the MET-3503 spec), so a link reproduces
 * the same results and the page works with no AI involved. Filters render as
 * editable chips; paging and sorting use the shared URL-backed table state,
 * whose keys are the spec's own `page` / `pageSize` / `sortBy` / `sortDir`.
 */
export function MeterSearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { customers, isLoading: customersLoading } = useCustomers();
  const { pagination, sorting, onPaginationChange, onSortingChange } = useTableUrlState({
    defaultPageSize: METER_SEARCH_DEFAULT_PAGE_SIZE,
  });

  const parsed = useMemo(() => parseMeterSearchParams(searchParams), [searchParams]);
  const spec = parsed.success ? parsed.data : null;
  const { data, isLoading, isFetching, error } = useMeterFilter(spec);

  const setSpec = (next: MeterSearch) => setSearchParams(toMeterSearchParams(next));

  return (
    <div className='space-y-6'>
      <div>
        <h1 className='text-2xl font-bold tracking-tight'>Meter Search</h1>
        <p className='text-muted-foreground'>
          Filter installed meters across customers. Share the URL to share the search.
        </p>
      </div>

      {!parsed.success
        ? (
          <Card>
            <CardHeader>
              <CardTitle>This search link isn't valid</CardTitle>
              <CardDescription>
                {parsed.error.issues.map((i) => `${i.path.join('.') || 'search'}: ${i.message}`)
                  .join('; ')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Link className='underline' to='/search/meters'>Start a new search</Link>
            </CardContent>
          </Card>
        )
        : (
          <Card>
            <CardHeader className='space-y-4'>
              <MeterSearchFilters
                spec={parsed.data}
                customers={customers}
                customersLoading={customersLoading}
                onChange={setSpec}
              />
              <CardDescription aria-live='polite'>
                {isLoading
                  ? 'Searching…'
                  : error
                  ? null
                  : `${(data?.total ?? 0).toLocaleString()} meter${data?.total === 1 ? '' : 's'}${
                    isFetching ? ' · updating…' : ''
                  }`}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {error
                ? (
                  <p role='alert' className='text-sm text-destructive py-4 text-center'>
                    Search failed: {error instanceof Error ? error.message : 'unknown error'}
                  </p>
                )
                : (
                  <DataTable
                    columns={columns}
                    data={data?.meters ?? []}
                    onRowClick={(meter) => navigate(`/meter-inspector/${meter.sensorId}`)}
                    manualPagination
                    rowCount={data?.total ?? 0}
                    pagination={pagination}
                    onPaginationChange={onPaginationChange}
                    manualSorting
                    sorting={sorting}
                    onSortingChange={onSortingChange}
                    emptyMessage={isLoading ? 'Searching…' : 'No meters match these filters.'}
                  />
                )}
            </CardContent>
          </Card>
        )}
    </div>
  );
}
